import Button from "../Button/button";
import React from "react";
import styles from "./activeCompetitions.module.css";
import { CompetitionData } from "./types";
import SkilledCompetitionStepper from "./skilled-competition-stepper";
import { CompetitionCategory } from "../../redux/features/categories/slice";
import ProgressBarComponent from "../ProgressBar/ProgressBarComponent";
import CompetitionItemBody from "./CompetitionItemBody";
import ExclusiveBadge from "../../(custom-header)/subscriber-competitions/components/exclusiveBadge";
import clsx from "clsx";
import CheckmarkIcon from "@/app/shared-components/icons/CheckmarkIcon";
import useCompetitionItem from "../../hooks/useCompetitionItem";

type CompetitionItemProps = {
  item: CompetitionData;
  isUserValidated: boolean;
  category: CompetitionCategory;
};

const CompetitionItem = ({ item, isUserValidated, category }: CompetitionItemProps) => {
  const {
    isSCCompetition,
    isMobile,
    isAlreadySubscribed,
    endsIn,
    setIsButtonHovered,
    ctaButtonText,
    onAddToBasketCTAClick,
    youveEnteredEntry,
    getStrokeColor,
    isSkilledCompetitionOnHomepage,
    isFullWidthRequired,
    onCompetitionClick,
  } = useCompetitionItem(item, isUserValidated, category);

  return (
    <div
      className={`${isSkilledCompetitionOnHomepage ? "col-span-2" : ""} min-h-[323px] lg:min-h-[494px] text-center bg-white flex flex-col h-full rounded-lg overflow-hidden ${styles.competitionCard}`}>
      <div className="relative rounded-t-lg">
        <a className="absolute z-[1] h-full w-full top-0 left-0" href={item.PageUrl}></a>
        <span
          className={`absolute z-[1] text-white font-jost lg:text-[12px] xs:text-[11px] lg:leading-4 xs:leading-[14px] font-medium uppercase lg:top-4 lg:left-4 xs:top-2 xs:left-2 xs:px-[6px] lg:px-2 py-[2px] ${styles.endsInText}`}>
          Ends {endsIn}
        </span>
        <div className="relative">
          <img
            // Both branches reserve their aspect ratio up front: the double-width skilled card
            // previously had none, so the card collapsed until the image decoded (layout shift).
            // 659/201 is the standard "competition-card-659x201" double-card crop.
            className={`w-full ${isSkilledCompetitionOnHomepage ? "aspect-[659/201] object-cover" : "aspect-[320/199] object-cover"}`}
            src={isSkilledCompetitionOnHomepage ? item.ImageDoubleUrl : item.ImageUrl}
            alt={item.Title}
            loading="lazy"
          />
          {isSkilledCompetitionOnHomepage && <SkilledCompetitionStepper isMobile={isMobile} />}
          {isSCCompetition ? <ExclusiveBadge className={"absolute bottom-0 right-0"} /> : null}
        </div>
        <div
          className={`w-full min-h-[28px] p-1.5 sm:min-h-[32px] relative text-center font-jost text-[10px] font-bold uppercase sm:py-[12px] px-[7px] sm:text-[14px] sm:px-[20px] text-ellipsis whitespace-nowrap overflow-hidden ${item.NavigationColour}`}>
          {item.Title} {item.AdditionalCashText}
        </div>
      </div>
      <div
        className={`${isSkilledCompetitionOnHomepage ? "grid grid-cols-2" : ""} bg-white self-stretch pt-3 px-2 pb-4 flex-1 h-full rounded-lg overflow-hidden lg:p-4 relative transition-all duration-200 ease-in-out hover-effect`}>
        {isSkilledCompetitionOnHomepage && (
          <img
            src="/assets/images/stb-competition-cta-badge.png"
            alt="Spot the Ball Competition Image"
            className="justify-self-center"
          />
        )}
        <div className=" flex flex-col h-full flex-1">
          <div className="text-[#28282B] text-center font-jost text-[12px] leading-4 lg:leading-5.5 font-medium mb-5 h-8 lg:h-[46px] sm:text-[16px]">
            {isAlreadySubscribed && isSCCompetition ? (
              <span className={"line-clamp-2"}>
                {"A thank you to our subscribers: automatic entry to the new daily draws!"}
              </span>
            ) : (
              <span className={"line-clamp-2"}>{isUserValidated ? item.SubtitleTextValidated : item.SubtitleText}</span>
            )}
          </div>
          {youveEnteredEntry && (
            <div className={"flex flex-col justify-center items-center mt-auto gap-1 text-[#308835] mb-5"}>
              <div
                className={
                  "self-stretch text-center text-[9px] font-medium leading-[10px] lg:text-[11px] lg:leading-5 uppercase"
                }>
                You entered with
              </div>
              <div className={"flex items-center gap-px lg:gap-1"}>
                <CheckmarkIcon variation={4} className={"size-5 shrink-0 text-[#308835]"} />{" "}
                <span
                  className={
                    "text-[#308835] text-center font-jost text-sm font-semibold leading-5 lg:text-xl lg:leading-5"
                  }>
                  {youveEnteredEntry?.nrOfTickets} {youveEnteredEntry?.nrOfTickets === 1 ? "ticket" : "tickets"}
                </span>
              </div>
            </div>
          )}
          <CompetitionItemBody item={item} />
          <div className={`${item.IsSinglePrizeMode && "lg:max-w-[200px] lg:min-w-[200px] lg:mx-auto min-h-5"}`}>
            <ProgressBarComponent item={item} />
          </div>

          <div className={"flex flex-col justify-center items-center mt-auto"}>
            {isSCCompetition ? (
              <button
                className={`uppercase flex justify-center items-center rounded px-2 xs:h-8 lg:h-10 btnWithArrow ${isAlreadySubscribed ? "border border-solid bg-white hover:bg-[#FF8200] text-[#FF8200] hover:text-white border-[#FF8200]" : "text-[#FF8200]"} uppercase min-w-[102px] lg:min-w-[148px]`}
                onClick={() => {
                  location.href = item.PageUrl;
                }}>
                <span
                  className={
                    "relative leading-[1] flex items-center font-jost xs:text-[12px] lg:text-[14px] max-w-48 font-medium"
                  }>
                  Details
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    className="arrowImage absolute left-[102%]">
                    <path
                      d="M5.22754 4.6665L8.56087 7.99984L5.22754 11.3332M9.22754 4.6665L12.5609 7.99984L9.22754 11.3332"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
            ) : null}

            <div
              className={`w-full flex justify-center items-center gap-2 lg:gap-3 ${isSCCompetition && isAlreadySubscribed ? "" : "lg:mt-3"}`}>
              <Button
                className={clsx(
                  "lg:min-w-[148px] bg-white hover:bg-[#FF8200] text-[#FF8200] hover:text-white border border-solid border-[#FF8200] uppercase flex justify-center items-center rounded px-2 xs:h-8 lg:h-10 btnWithArrow",
                  {
                    ["w-full"]: isFullWidthRequired.current || isMobile,
                    ["lg:min-w-[200px] !bg-[#FF8200] !hover:bg-[#ff6a00] !text-white"]: isSkilledCompetitionOnHomepage,
                    [`${isAlreadySubscribed ? "hidden" : "!bg-[#FF8200] !hover:bg-[#ff6a00] !text-white"}`]:
                      isSCCompetition,
                  }
                )}
                onClick={onCompetitionClick}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                text="">
                <span className="relative leading-[1] flex items-center font-jost xs:text-[12px] lg:text-[14px] max-w-48 font-medium">
                  {ctaButtonText}
                  {!item.IsSinglePrizeMode && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                      className={`arrowImage absolute left-[102%] ${isSCCompetition && !isAlreadySubscribed ? "hidden" : ""}`}>
                      <path
                        d="M5.22754 4.6665L8.56087 7.99984L5.22754 11.3332M9.22754 4.6665L12.5609 7.99984L9.22754 11.3332"
                        stroke={getStrokeColor()}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </Button>
              {item.IsSinglePrizeMode && (
                <Button
                  className="bg-[#ff8200] rounded border-0 xs:h-8 lg:h-10 xs:w-12 lg:w-10 flex justify-center items-center"
                  onClick={onAddToBasketCTAClick}
                  text="">
                  {isMobile ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
                      <path
                        d="M5.00065 14.7071C5.44268 14.7071 5.8666 14.8827 6.17916 15.1952C6.49172 15.5078 6.66732 15.9317 6.66732 16.3737C6.66732 16.8158 6.49172 17.2397 6.17916 17.5522C5.8666 17.8648 5.44268 18.0404 5.00065 18.0404C4.55862 18.0404 4.1347 17.8648 3.82214 17.5522C3.50958 17.2397 3.33398 16.8158 3.33398 16.3737C3.33398 15.9317 3.50958 15.5078 3.82214 15.1952C4.1347 14.8827 4.55862 14.7071 5.00065 14.7071ZM5.00065 14.7071H14.1673M5.00065 14.7071V3.04041H3.33398M14.1673 14.7071C14.6093 14.7071 15.0333 14.8827 15.3458 15.1952C15.6584 15.5078 15.834 15.9317 15.834 16.3737C15.834 16.8158 15.6584 17.2397 15.3458 17.5522C15.0333 17.8648 14.6093 18.0404 14.1673 18.0404C13.7253 18.0404 13.3014 17.8648 12.9888 17.5522C12.6762 17.2397 12.5007 16.8158 12.5007 16.3737C12.5007 15.9317 12.6762 15.5078 12.9888 15.1952C13.3014 14.8827 13.7253 14.7071 14.1673 14.7071ZM5.00065 4.70708L16.6673 5.54041L15.834 11.3737H5.00065"
                        stroke="#F7F7F7"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M3.92969 14.921C3.92969 15.3398 4.09604 15.7414 4.39215 16.0375C4.68826 16.3336 5.08987 16.4999 5.50863 16.4999C5.9274 16.4999 6.32901 16.3336 6.62512 16.0375C6.92123 15.7414 7.08758 15.3398 7.08758 14.921C7.08758 14.5022 6.92123 14.1006 6.62512 13.8045C6.32901 13.5084 5.9274 13.342 5.50863 13.342C5.08987 13.342 4.68826 13.5084 4.39215 13.8045C4.09604 14.1006 3.92969 14.5022 3.92969 14.921Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12.6113 14.921C12.6113 15.3398 12.7777 15.7414 13.0738 16.0375C13.3699 16.3336 13.7715 16.4999 14.1903 16.4999C14.609 16.4999 15.0107 16.3336 15.3068 16.0375C15.6029 15.7414 15.7692 15.3398 15.7692 14.921C15.7692 14.5022 15.6029 14.1006 15.3068 13.8045C15.0107 13.5084 14.609 13.342 14.1903 13.342C13.7715 13.342 13.3699 13.5084 13.0738 13.8045C12.7777 14.1006 12.6113 14.5022 12.6113 14.921Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14.1921 13.3419H5.50792V1.49976H1.56055"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.50977 3.86938L16.5624 4.65886L15.7729 10.1852H5.50977"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitionItem;
